import React, { useRef } from 'react';
import { UnsavedChangesGuard } from '@RichTextEditor';
import { FlexLayout, Spacer } from '@Styled/utilities';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Content } from '../../Types/WebContent.types';
import { CONTENT_SCHEMA } from '../../Schema/webContent.schema';
import {
	Button,
	ButtonGroup,
	Grow,
	InputAdornment,
	MenuItem,
	MenuList,
	Paper,
	Popper,
} from '@mui/material';
import { WebContentRequester } from '../../Services/WebContent.requester';
import { useSnackbar } from '@Providers/useSnackbar';
import { useHistory } from 'react-router-dom';
import { useTypedSelector } from '@Features/store';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import { ContentConfig } from '../../../../Constants/CourseContent.constants';
import { DevTool } from '@hookform/devtools';
import RichTextEditor from '@Components/UI/RichTextEditor/RichTextEditor';
import styled from 'styled-components';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { UnitTypeId } from '@Constants/unitTypes';

const AddWebContent = () => {
	const editorRef = useRef<any>(null);
	const { displaySnackbar } = useSnackbar();
	const history = useHistory();

	const { currentCourse } = useTypedSelector((state) => state.classroom);

	const { control, formState, handleSubmit, reset } = useForm<Content>({
		mode: 'all',
		resolver: yupResolver(CONTENT_SCHEMA),
		defaultValues: {
			unit: {
				type_id: UnitTypeId.webcontent,
				course_id: currentCourse?.id,
			},
		},
	});

	const onSave = async (form: Content) => {
		try {
			const {
				data: { data },
			} = await WebContentRequester.instance.create({
				...form,
			});
			displaySnackbar('success', 'Created Web Content successfully');
			history.goBack();
		} catch (e) {
			const msg = e.response?.data.message || 'Unable to Create Web Content ';
			displaySnackbar('error', msg);
		}
	};

	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current?.contains(event.target as HTMLElement)) {
			return;
		}
		setOpen(false);
	};

	const handleReset = () => {
		reset({
			unit: {
				type_id: UnitTypeId.webcontent,
				course_id: currentCourse?.id,
				name: '',
			},
			webContent: {
				content: '',
			},
		});
	};

	const onSaveAddAnother = async (form: Content) => {
		try {
			const {
				data: { data },
			} = await WebContentRequester.instance.create({
				...form,
			});
			displaySnackbar('success', 'Created Web Content successfully');
			handleReset();
		} catch (e) {
			const msg = e.response?.data.message || 'Unable to Create Web Content ';
			displaySnackbar('error', msg);
		}
	};

	// Redux-sourced course id is empty right after a hard refresh until the
	// parent course route repopulates it; without it the unit would save
	// with course_id undefined.
	if (!currentCourse?.id) return null;

	return (
		<FlexLayout flexDirection="column" px="2.625rem" flex="1">
			<UnsavedChangesGuard
				formState={formState}
			/>
			<EdFormControl
				size="small"
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							{ContentConfig['webcontent'].icon}
						</InputAdornment>
					),
				}}
				control={control}
				label="Unit Name"
				name="unit.name"
			/>
			<Spacer mb="1.625rem" />

			<FlexLayout flex="1">
				<Controller
					control={control}
					name="webContent.content"
					render={({ field: { value, onChange, ...props } }) => {
						return (
							<RichTextEditor preset="full"
								{...props}
								value={value}
								onChange={onChange}
							/>
						);
					}}
				/>
			</FlexLayout>
			<FlexLayout mt="1.688rem" justifyContent="flex-end">
				<Button
					variant="contained"
					color="warning"
					onClick={() => history.goBack()}
				>
					Cancel
				</Button>
				<Spacer mx="0.5rem" />
				<ButtonGroup
					variant="contained"
					ref={anchorRef}
					aria-label="split button"
				>
					<Button
						onClick={handleSubmit(onSave)}
						disabled={
							!formState.isDirty ||
							!formState.isValid ||
							formState.isSubmitting
						}
						variant="contained"
					>
						Save
					</Button>
					<DropButton
						size="small"
						aria-controls={open ? 'split-button-menu' : undefined}
						aria-expanded={open ? 'true' : undefined}
						onClick={handleToggle}
						variant="contained"
						disabled={!formState.isDirty || !formState.isValid}
					>
						<ArrowDropDownIcon />
					</DropButton>
				</ButtonGroup>
				<Popper
					sx={{
						zIndex: 1,
					}}
					open={open}
					anchorEl={anchorRef.current}
					role={undefined}
					transition
					disablePortal
				>
					{({ TransitionProps, placement }) => (
						<Grow
							{...TransitionProps}
							style={{
								transformOrigin:
									placement === 'bottom' ? 'center top' : 'center bottom',
							}}
						>
							<Paper>
								<ClickAwayListener onClickAway={handleClose}>
									<MenuList id="split-button-menu" autoFocusItem>
										<MenuItem
											key={1}
											disabled={formState.isSubmitting}
											onClick={handleSubmit(onSaveAddAnother)}
										>
											Save and add another
										</MenuItem>
									</MenuList>
								</ClickAwayListener>
							</Paper>
						</Grow>
					)}
				</Popper>
			</FlexLayout>
		</FlexLayout>
	);
};

export const DropButton = styled(Button)`
	min-width: 30px !important;
	padding: 0;
`;

export default AddWebContent;
